export {suppressNextCompatibleClick} from '@fitro/overlay-layout'

type PointerLikeEvent = {
    pointerType: string
    button?: number
}

export function isPenPointer(event: PointerLikeEvent) {
    return event.pointerType === 'pen'
}

function isEditableElement(element: Element | null): element is HTMLElement {
    if (!(element instanceof HTMLElement)) return false
    if (element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement) return true
    if (element instanceof HTMLInputElement) {
        return element.type !== 'button' &&
            element.type !== 'checkbox' &&
            element.type !== 'color' &&
            element.type !== 'file' &&
            element.type !== 'hidden' &&
            element.type !== 'image' &&
            element.type !== 'radio' &&
            element.type !== 'range' &&
            element.type !== 'reset' &&
            element.type !== 'submit'
    }
    return element.isContentEditable ||
        element.getAttribute('contenteditable') === '' ||
        element.getAttribute('contenteditable') === 'true' ||
        element.getAttribute('contenteditable') === 'plaintext-only'
}

const EDITABLE_TARGET_SELECTOR = 'input, textarea, select, [contenteditable]'

function isInsideEditableElement(element: Element | null) {
    let current: Element | null = element

    while (current) {
        if (isEditableElement(current)) return true
        current = current.parentElement
    }

    return false
}

export function blurActiveEditableOnTouchPointerOutside(
    event: PointerLikeEvent & {target: EventTarget | null; nativeEvent?: PointerLikeEvent}
) {
    const pointerType = event.pointerType || event.nativeEvent?.pointerType
    if ((pointerType !== 'touch' && pointerType !== 'pen') || typeof document === 'undefined') {
        return false
    }

    const activeElement = document.activeElement
    if (!isEditableElement(activeElement)) return false

    const target = event.target instanceof Element ? event.target : null
    if (!target || activeElement === target || activeElement.contains(target)) return false
    if (target.closest('[data-fitro-selection-overlay="true"]')) return false
    const editableCandidate = target.closest(EDITABLE_TARGET_SELECTOR)
    if (isInsideEditableElement(editableCandidate)) {
        return false
    }

    activeElement.blur()
    return true
}

type MutableRef<T> = {
    current: T
}

export function setTimedClickSuppression<T>(
    suppressRef: MutableRef<T | null>,
    value: T,
    timeoutRef: MutableRef<number | null>,
    timeoutMs = 750
) {
    suppressRef.current = value

    if (typeof window === 'undefined') return

    if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = window.setTimeout(() => {
        if (suppressRef.current === value) {
            suppressRef.current = null
        }
        timeoutRef.current = null
    }, timeoutMs)
}

export function consumeTimedClickSuppression<T>(
    suppressRef: MutableRef<T | null>,
    expectedValue: T,
    timeoutRef: MutableRef<number | null>
) {
    if (suppressRef.current !== expectedValue) {
        return false
    }

    suppressRef.current = null

    if (typeof window !== 'undefined' && timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current)
        timeoutRef.current = null
    }

    return true
}
